import { FC, useState, MouseEvent } from 'react';
import { useFragment, useMutation, graphql } from 'react-relay';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { ZipCode } from 'dibs-address-form';
import { BasicSelect } from 'dibs-elements/exports/BasicSelect';
import { Button } from 'dibs-elements/exports/Button';
import { validateField } from 'dibs-address-validator/exports/validate';
import { trackEvent } from 'dibs-tracking';

import styles from './ShippingDetailsBuyerLocationForm.scss';

import { ShippingDetailsBuyerLocationForm_viewer$key } from './__generated__/ShippingDetailsBuyerLocationForm_viewer.graphql';
import { ShippingDetailsBuyerLocationForm_item$key } from './__generated__/ShippingDetailsBuyerLocationForm_item.graphql';
import { shippingDetailsLocationAddPartialAddressMutation } from '../../mutations/shippingDetailsLocationAddPartialAddress';
import { shippingDetailsLocationAddPartialAddressMutation as shippingDetailsLocationAddPartialAddressMutationType } from '../../mutations/__generated__/shippingDetailsLocationAddPartialAddressMutation.graphql';

type Props = {
    viewer: ShippingDetailsBuyerLocationForm_viewer$key;
    item: ShippingDetailsBuyerLocationForm_item$key;
    userId: string;
    location: { zipCode: string; country: string };
    onAddressSubmit: (address: { country: string; zipCode: string }) => void;
    isDisabled: boolean;
};

const ShippingDetailsBuyerLocationForm: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    location,
    userId,
    isDisabled,
    onAddressSubmit,
}) => {
    const intl = useIntl();
    const viewer = useFragment(
        graphql`
            fragment ShippingDetailsBuyerLocationForm_viewer on Viewer {
                geography {
                    countries {
                        name
                        abbreviation
                    }
                }
            }
        `,
        viewerRef
    );
    const item = useFragment(
        graphql`
            fragment ShippingDetailsBuyerLocationForm_item on Item {
                address {
                    itemAddress: displayCityStateCountry(fullCountryName: true)
                }
            }
        `,
        itemRef
    );
    const [commitAddPartialAddress, isAddPartialAddressInFlight] =
        useMutation<shippingDetailsLocationAddPartialAddressMutationType>(
            shippingDetailsLocationAddPartialAddressMutation
        );

    const { geography } = viewer;
    const countries = (geography?.countries || []).map(country => ({
        label: country?.name || '',
        value: country?.abbreviation || '',
    }));

    const [currentLocation, setCurrentLocation] = useState(location);
    const [countryError, setCountryError] = useState<string | null>(null);
    const [zipCodeError, setZipCodeError] = useState<string | null>(null);
    const [showFormError, setShowFormError] = useState(false);
    const isCountryUS = currentLocation.country === 'US';
    const itemLocation = item?.address?.itemAddress;

    const validateFields = ({ country, zipCode }: { country: string; zipCode: string }): void => {
        const zipCodeValidation = validateField(zipCode, {
            field: 'zipCode',
            isCountryUS: country === 'US',
        });

        const countryValidation = validateField(country, {
            field: 'country',
            isCountryUS: country === 'US',
        });

        setCountryError(
            countryValidation?.error ? intl.formatMessage(countryValidation?.error) : null
        );

        setZipCodeError(
            zipCodeValidation?.error ? intl.formatMessage(zipCodeValidation?.error) : null
        );
    };

    const onChange = ({ name, value }: { name: string; value: string }): void => {
        const updatedLocation = { ...currentLocation, [name]: value };
        setCurrentLocation(updatedLocation);
        validateFields(updatedLocation);
    };

    const onSubmit = (event: MouseEvent): void => {
        setShowFormError(false);
        const { country, zipCode } = currentLocation;
        const isFormValid = !(zipCodeError || countryError);
        if ((location.country !== country || location.zipCode !== zipCode) && isFormValid) {
            trackEvent(
                {
                    category: 'shipping options',
                    action: 'shipping options location updated',
                    label: `${country} | ${zipCode}`,
                },
                event
            );
            if (userId) {
                commitAddPartialAddress({
                    variables: {
                        input: {
                            userId: userId,
                            address: {
                                zipCode,
                                country,
                            },
                        },
                    },
                    onCompleted: () => {
                        onAddressSubmit(currentLocation);
                    },
                    onError: () => {
                        setShowFormError(true);
                    },
                });
            } else {
                onAddressSubmit(currentLocation);
            }
        }
    };

    return (
        <div>
            {itemLocation && (
                <div className={styles.addressWrapper}>
                    <span className={styles.label}>
                        <FormattedMessage
                            id="requestShippingModal.ShippingDetailsBuyerLocationForm.shippingFrom"
                            defaultMessage="Ships from:"
                        />
                    </span>{' '}
                    {itemLocation}
                </div>
            )}
            <div className={styles.label}>
                <FormattedMessage
                    id="requestShippingModal.ShippingDetailsBuyerLocationForm.shippingTo"
                    defaultMessage="Shipping to:"
                />
            </div>
            <div className={styles.locationFormWrapper}>
                <div className={styles.countryInput}>
                    <BasicSelect
                        name="country"
                        dataTn="shipping-details-location-form-country"
                        value={currentLocation.country}
                        options={countries}
                        size="medium"
                        disabled={isDisabled || isAddPartialAddressInFlight}
                        onChange={event => {
                            onChange({ name: 'country', value: event.target.value });
                        }}
                        errorMessage={countryError}
                    />
                </div>
                <ZipCode
                    error={zipCodeError}
                    disabled={isDisabled || isAddPartialAddressInFlight}
                    isBlurred
                    value={currentLocation.zipCode}
                    fieldSize="medium"
                    isCountryUS={isCountryUS}
                    onChange={({ value }) => {
                        onChange({ name: 'zipCode', value });
                    }}
                    wrapperClass={styles.zipCodeInput}
                />
                <div className={styles.buttonWrapper}>
                    <Button
                        dataTn="shipping-details-location-form-button"
                        onClick={event => onSubmit(event)}
                        disabled={isDisabled || isAddPartialAddressInFlight}
                        size="medium"
                        fullWidth
                    >
                        <FormattedMessage
                            id="requestShippingModal.ShippingDetailsBuyerLocationForm.submitButton"
                            defaultMessage="Apply"
                        />
                    </Button>
                </div>
            </div>
            {showFormError && (
                <div className={styles.errorMessage}>
                    <FormattedMessage
                        id="requestShippingModal.ShippingDetailsBuyerLocationForm.formErrorMessage"
                        defaultMessage="An error occurred."
                    />
                </div>
            )}
        </div>
    );
};

export default ShippingDetailsBuyerLocationForm;
